import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import {
  API_COURSES_ADMIN,
  AtRiskStudentsResponse,
  AttendanceDetail,
  BulkMarkAttendancePayload,
  BulkMarkAttendanceResponse,
  CancelAndReschedulePayload,
  CancelAndRescheduleResponse,
  CancelSeriesPayload,
  CancelSeriesResponse,
  CancelSessionPayload,
  CourseDetailsModelV2,
  EventType,
  LiveSessionCourseModel,
  LiveSessionCourseUpsertData,
  MarkAttendancePayload,
  Pagination,
  Params,
  PostSessionRecapResponse,
  ScheduleSessionLists,
  SeriesAnalytics,
  SessionAnalytics,
  SessionAttendanceRecord,
  SessionAttendanceResponse,
  SessionChatResponse,
  SingleSessionSettings,
  StudentAnalyticsResponse,
  StudyMaterial,
  StudyMaterialsApiResponse
} from '../../models';
import {
  GlobalStudentAnalyticsResponse,
} from '../../models/v2/attendance.model';
import {
  API_COURSES_INSTRUCTOR,
  API_COURSES_LIVE_GET,
  API_COURSES_LIVE_LIST,
  API_COURSES_LIVE_PARTICIPANTS,
  API_COURSES_LIVE_SESSIONS,
  API_COURSES_LIVE_SETTINGS_GET,
  API_COURSES_LIVE_SETTINGS_UPDATE,
  API_COURSES_UPDATE,
  API_DELETE_LIVE_COURSE_ANNOUNCEMENTS,
  API_EVENT_TYPE_CHECK_SLUG,
  API_EVENT_TYPE_GET,
  API_EVENT_TYPE_UPDATE,
  API_GET_LIVE_COURSE_ANNOUNCEMENTS_BY_ANNOUNCEMENT_ID,
  API_GET_LIVE_COURSE_ANNOUNCEMENTS_BY_PROJECTS_ID,
  API_GET_LIVE_SESSION_COURSE_DETAILS,
  API_POST_LIVE_COURSE_ANNOUNCEMENTS,
  API_POST_LIVE_SESSION_SUBMIT_FOR_REVIEW,
  API_PROJECT_AVAILABLE_SLOTS,
  API_PROJECT_CHECK_CONFLICTS,
  API_PROJECT_GENERATE_MULTIPLE_SESSIONS,
  API_PROJECT_GENERATE_SINGLE_SESSIONS,
  API_PROJECT_PREFERRED_SLOTS,
  API_PROJECT_SCHEDULE,
  API_PUT_LIVE_COURSE_ANNOUNCEMENTS_BY_ANNOUNCEMENT_ID,
  API_SCHEDULE_CREATE,
  API_SCHEDULE_DELETE,
  API_SCHEDULE_GET,
  API_SCHEDULE_LIST,
  API_SCHEDULE_UPDATE,
  API_SERIES_ANALYTICS,
  API_SERIES_AT_RISK_STUDENTS,
  API_SERIES_CANCEL,
  API_SERIES_DELETE,
  API_SERIES_PREVIEW_COPY,
  API_SERIES_PREVIEW_SESSIONS,
  API_SERIES_MY_BOOKINGS,
  API_SERIES_SESSIONS,
  API_SERIES_UPDATE,
  API_SESSION_AI_SUMMARY,
  API_SESSION_AI_TRANSCRIPTS,
  API_STUDENT_SESSION_AI_SUMMARY,
  API_STUDENT_SESSION_AI_TRANSCRIPTS,
  API_SESSION_ANALYTICS,
  API_SESSION_ATTENDANCE,
  API_SESSION_ATTENDANCE_BULK_MARK,
  API_SESSION_ATTENDANCE_DETAIL,
  API_SESSION_ATTENDANCE_MARK,
  API_SESSION_CALENDAR_LINKS,
  API_SESSION_CANCEL,
  API_SESSION_CHAT,
  API_SESSION_DELETE,
  API_SESSION_GET,
  API_SESSION_MAKEUP,
  API_SESSION_RESCHEDULE,
  API_SESSION_UPDATE,
  API_STUDENT_GLOBAL_ANALYTICS,
  API_STUDENT_SERIES_ANALYTICS,
  API_STUDENT_SESSION_RECAP,
  API_STUDY_MATERIALS_DELETE,
  API_STUDY_MATERIALS_GET,
  API_STUDY_MATERIALS_RENAME,
} from '../../models/constants';

import { encodeURL } from '../../utils';
import { HttpService } from '../http';
import { Logger } from '../logger';
const log = new Logger('InstructorService');

@Injectable({
  providedIn: 'root',
})
export class LiveSessionService {
  private readonly http = inject(HttpService);

  // **Instructor**
  getInstructorLiveSessionCourse(courseId: string, params: Params): Observable<CourseDetailsModelV2> {
    return this.http.get<CourseDetailsModelV2>(
      encodeURL(API_GET_LIVE_SESSION_COURSE_DETAILS, params).replace('<COURSE_ID>', courseId)
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → CourseDetailsModelV2:', resp)));
  }

  getCourses(params = {} as object): Observable<Pagination<LiveSessionCourseModel>> {
    const variable = environment.isAdmin ? API_COURSES_ADMIN : API_COURSES_LIVE_LIST;
    return this.http.get<Pagination<LiveSessionCourseModel>>(encodeURL(variable + '/', params));
  }

  getCourse(projectId: string, params: Params): Observable<{ data: LiveSessionCourseModel }> {
    return this.http.get<{ data: LiveSessionCourseModel }>(
      encodeURL(API_COURSES_LIVE_GET.replace('<PROJECT_ID>', projectId), params)
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → LiveSessionCourseModel:', resp)));
  }

  createCourseV2(data: any): Observable<CourseDetailsModelV2> {
    return this.http.post(API_COURSES_INSTRUCTOR + '/', data);
  }

  // live-session.service.ts
  updateEventType(projectId: string, payload: any): Observable<CourseDetailsModelV2> {
    return this.http.patch(API_EVENT_TYPE_UPDATE.replace('<PROJECT_ID>', projectId), payload);
  }

  checkCustomSlug(slug: string, excludeProject?: string): Observable<{ available: boolean }> {
    const params: Record<string, string> = { slug };
    if (excludeProject) {
      params['exclude_project'] = excludeProject;
    }
    return this.http.get<{ available: boolean }>(API_EVENT_TYPE_CHECK_SLUG, params);
  }

  getLiveSessionClassSetup(projectId: string, params: Params): Observable<{ data: EventType }> {
    return this.http.get<{ data: EventType }>(encodeURL(API_EVENT_TYPE_GET.replace('<PROJECT_ID>', projectId), params));
    //.pipe(tap((resp) => console.log('⬅️ Service Response → EventType:', resp)));
  }
  getAvailabilityMySchedules(params: Params): Observable<{ data: any }> {
    return this.http.get<{ data: EventType }>(encodeURL(API_SCHEDULE_LIST, params));
    //.pipe(tap((resp) => console.log('⬅️ Service Response → AvailabilityMySchedules:', resp)));
  }

  getSpecificSchedule(id: any, params: Params): Observable<{ data: any }> {
    return this.http.get<{ data: any }>(encodeURL(API_SCHEDULE_GET.replace('<ID>', id), params));
  }

  createSchedule(payload: any): Observable<CourseDetailsModelV2> {
    return this.http.post(API_SCHEDULE_CREATE, payload);
  }
  updateSchedule(id: any, payload: any): Observable<CourseDetailsModelV2> {
    return this.http.put(API_SCHEDULE_UPDATE.replace('<ID>', id), payload);
  }

  deleteSchedule(id: number): Observable<void> {
    return this.http.delete<void>(API_SCHEDULE_DELETE.replace('<ID>', id.toString()));
  }

  /**
   * Unified endpoint to get project schedule (sessions or series based on course_mode).
   *
   * Query params:
   * - status: Filter by status (comma-separated): draft,ready,completed,expired,cancelled
   * - include_archived: Include completed/expired/cancelled items (default: false)
   * - ordering: Sort by: start_date, -start_date, name, -name, created, -created
   * - search: Search by name
   * - page, page_size: Pagination
   */
  getProjectSchedule(projectId: string, params: Params = {}) {
    return this.http.get<{
      status: string;
      course_mode: 'SINGLE_SESSION' | 'MULTIPLE_SESSION';
      project_id: string;
      count: number;
      next: string | null;
      previous: string | null;
      results: any[];
    }>(encodeURL(API_PROJECT_SCHEDULE.replace('<PROJECT_ID>', projectId), params));
  }

  getSeriesSingleSessionById(seriesId: number, params: Params) {
    return this.http.get<{ data: any }>(
      encodeURL(API_SERIES_SESSIONS.replace('<SERIES_ID>', seriesId.toString()), params)
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getSeries:', resp)));
  }

  /** Hydrate the booking confirmation page on cold loads — returns the
   *  series sessions (and product id) for any user with a CONFIRMED booking
   *  on the series. 403 if the requesting user has no booking. */
  getSeriesMyBookings(seriesId: number): Observable<{
    series_id: number;
    product_id: string;
    sessions: { id: number; start_datetime: string; end_datetime: string }[];
  }> {
    return this.http.get<{
      series_id: number;
      product_id: string;
      sessions: { id: number; start_datetime: string; end_datetime: string }[];
    }>(API_SERIES_MY_BOOKINGS.replace('<SERIES_ID>', seriesId.toString()));
  }

  getParticipants(projectId: string, params: Params) {
    return this.http.get<{ data: any }>(
      encodeURL(API_COURSES_LIVE_PARTICIPANTS.replace('<PROJECT_ID>', projectId), params)
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getSeries:', resp)));
  }

  // live-session.service.ts
  updateSeriesStatus(seriesId: number, payload: any): Observable<CourseDetailsModelV2> {
    return this.http.patch(API_SERIES_UPDATE.replace('<SERIES_ID>', seriesId.toString()), payload);
  }

  updateSingleSession(sessionId: number, payload: any): Observable<any> {
    return this.http.patch(API_SESSION_UPDATE.replace('<SESSION_ID>', sessionId.toString()), payload);
  }

  getpreferredTimeSlots(projectId: string, payload: any) {
    return this.http.get(API_PROJECT_PREFERRED_SLOTS.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getpreferredTimeSlots:', resp)));
  }

  checkSessionConflicts(projectId: string, payload: any) {
    return this.http.post(API_PROJECT_CHECK_CONFLICTS.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → checkConflicts:', resp)));
  }

  previewSessions(projectId: string, payload: any) {
    return this.http.post(API_SERIES_PREVIEW_SESSIONS.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → previewSessions:', resp)));
  }

  submitForReview(projectId: string, payload: any) {
    return this.http.post(API_POST_LIVE_SESSION_SUBMIT_FOR_REVIEW.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → submitForReview:', resp)));
  }
  previewCopySessions(seriesId: string, payload: any) {
    return this.http.post(API_SERIES_PREVIEW_COPY.replace('<SERIES_ID>', seriesId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → previewCopySessions:', resp)));
  }

  generateMultipleSessions(projectId: string, payload: any) {
    return this.http.post(API_PROJECT_GENERATE_MULTIPLE_SESSIONS.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → generateMultipleSessions:', resp)));
  }

  deleteSeries(projectId: string, seriesId: number) {
    return this.http.delete(API_SERIES_DELETE.replace('<SERIES_ID>', seriesId.toString()));
    //.pipe(tap((resp) => console.log('⬅️ Service Response → deleteSeries:', resp)));
  }

  deleteSession(sessionId: number) {
    return this.http.delete(API_SESSION_DELETE.replace('<SESSION_ID>', sessionId.toString()));
    //.pipe(tap((resp) => console.log('⬅️ Service Response → deleteSession:', resp)));
  }

  getAvailableSlots(projectId: string, params: any) {
    return this.http.get<{ data: any }>(
      encodeURL(API_PROJECT_AVAILABLE_SLOTS.replace('<PROJECT_ID>', projectId), params)
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getAvailableSlots:', resp)));
  }

  getSingleSessionSettings(projectId: string, params: any) {
    return this.http.get<{ data: SingleSessionSettings }>(
      encodeURL(API_COURSES_LIVE_SETTINGS_GET.replace('<PROJECT_ID>', projectId), params)
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getSingleSessionSettings:', resp)));
  }

  updateSingleSessionSettings(projectId: string, payload: any) {
    return this.http.put<{ data: SingleSessionSettings }>(
      API_COURSES_LIVE_SETTINGS_UPDATE.replace('<PROJECT_ID>', projectId),
      payload
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → updateSingleSessionSettings:', resp)));
  }

  generateSingleSession(projectId: string, payload: any) {
    return this.http.post(API_PROJECT_GENERATE_SINGLE_SESSIONS.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → generateSingleSession:', resp)));
  }

  generateMakeupSession(sessionId: number, payload: any) {
    return this.http.post(API_SESSION_MAKEUP.replace('<SESSION_ID>', sessionId.toString()), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → generateSingleSession:', resp)));
  }

  getSessionById(sessionId: string, payload: any) {
    return this.http.get<{ data: ScheduleSessionLists }>(API_SESSION_GET.replace('<SESSION_ID>', sessionId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getSessionById:', resp)));
  }

  // Upsert live session
  updateLiveSessionCourse(courseId: string, data: LiveSessionCourseUpsertData): Observable<CourseDetailsModelV2> {
    return this.http.put(API_COURSES_UPDATE.replace('<COURSE_ID>', courseId), data);
  }

  //schedule list
  getLiveCourseSessions(params: any) {
    return this.http.get<{ data: ScheduleSessionLists }>(encodeURL(API_COURSES_LIVE_SESSIONS, params));
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getSingleSessionSettings:', resp)));
  }
  //announcements create by projectId
  createLiveCourseAnnouncementsByProjectId(projectId: string, payload: any) {
    return this.http.post(API_POST_LIVE_COURSE_ANNOUNCEMENTS.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → createLiveCourseAnnouncementsByProjectId:', resp)));
  }
  //announcements by projectId
  getLiveCourseAnnouncementsByProjectId(projectId: string, payload: any) {
    return this.http.get(API_GET_LIVE_COURSE_ANNOUNCEMENTS_BY_PROJECTS_ID.replace('<PROJECT_ID>', projectId), payload);
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getLiveCourseAnnouncementsByProjectId:', resp)));
  }
  //announcements by AnnouncementId
  getLiveCourseAnnouncementsById(announcementId: number, payload: any) {
    return this.http.get(
      API_GET_LIVE_COURSE_ANNOUNCEMENTS_BY_ANNOUNCEMENT_ID.replace('<ANNOUNCEMENT_ID>', announcementId.toString()),
      payload
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → getLiveCourseAnnouncementsById:', resp)));
  }
  // Announcement Update
  updateLiveCourseAnnouncementsById(announcementId: number, data: any): Observable<any> {
    return this.http.put(
      API_PUT_LIVE_COURSE_ANNOUNCEMENTS_BY_ANNOUNCEMENT_ID.replace('<ANNOUNCEMENT_ID>', announcementId.toString()),
      data
    );
  }
  //Announcement delete
  deleteLiveCourseAnnouncementsById(announcementId: number) {
    return this.http.delete(
      API_DELETE_LIVE_COURSE_ANNOUNCEMENTS.replace('<ANNOUNCEMENT_ID>', announcementId.toString())
    );
    //.pipe(tap((resp) => console.log('⬅️ Service Response → deleteLiveCourseAnnouncementsById:', resp)));
  }

  // Cancel and reschedule a session
  cancelAndRescheduleSession(
    sessionId: number,
    payload: CancelAndReschedulePayload
  ): Observable<CancelAndRescheduleResponse> {
    return this.http.post<CancelAndRescheduleResponse>(
      API_SESSION_RESCHEDULE.replace('<SESSION_ID>', sessionId.toString()),
      payload
    );
  }

  // ==================== Session Cancellation ====================

  /**
   * Cancel a session without rescheduling.
   * Backend enforces business rules: makeup sessions always cancellable,
   * regular sessions cancellable when no enrollments, course is truly free,
   * or admin has granted cancel permission.
   */
  cancelSession(sessionId: number, payload: CancelSessionPayload): Observable<{ status: string; data: unknown }> {
    return this.http.post<{ status: string; data: unknown }>(
      API_SESSION_CANCEL.replace('<SESSION_ID>', sessionId.toString()),
      payload
    );
  }

  /**
   * Cancel an entire session series
   * @note This feature is prepared for future use - DO NOT expose in UI yet
   */
  cancelSeries(seriesId: number, payload: CancelSeriesPayload): Observable<CancelSeriesResponse> {
    return this.http.post<CancelSeriesResponse>(API_SERIES_CANCEL.replace('<SERIES_ID>', seriesId.toString()), payload);
  }

  // ==================== Study Materials ====================

  /**
   * Get study materials for a project
   * @param projectId - The project ID
   * @param params - Query params including optional session_id
   */
  getStudyMaterials(projectId: string, params: Params = {}): Observable<StudyMaterialsApiResponse> {
    return this.http.get<StudyMaterialsApiResponse>(
      encodeURL(API_STUDY_MATERIALS_GET.replace('<PROJECT_ID>', projectId), params)
    );
  }

  /**
   * Delete a study material
   * @param id - The study material ID (can be string or number)
   */
  deleteStudyMaterial(id: number | string): Observable<void> {
    return this.http.delete<void>(API_STUDY_MATERIALS_DELETE.replace('<ID>', id.toString()));
  }

  /**
   * Rename a study material
   * @param id - The study material ID (can be string or number)
   * @param payload - Object containing the new label
   */
  renameStudyMaterial(id: number | string, payload: { label: string }): Observable<StudyMaterial> {
    return this.http.patch<StudyMaterial>(API_STUDY_MATERIALS_RENAME.replace('<ID>', id.toString()), payload);
  }

  // ==================== Chat ====================

  /**
   * Get chat messages for a session
   * @param sessionId - The session ID
   * @param params - Query params (page, page_size, etc.)
   */
  getSessionChat(sessionId: string, params: Params = {}): Observable<SessionChatResponse> {
    return this.http.get<SessionChatResponse>(
      encodeURL(API_SESSION_CHAT.replace('<SESSION_ID>', sessionId), params)
    );
  }

  // ==================== Attendance Management ====================

  /**
   * Get session attendance list with summary (instructor only)
   * @param sessionId - The session ID
   * @param params - Query params: status, search, ordering, page
   */
  getSessionAttendance(sessionId: number | string, params: Params = {}) {
    return this.http.get<SessionAttendanceResponse>(
      encodeURL(API_SESSION_ATTENDANCE.replace('<SESSION_ID>', sessionId.toString()), params)
    );
  }

  /**
   * Mark a single attendance record (instructor only)
   * @param sessionId - The session ID
   * @param attendanceId - The attendance record ID
   * @param data - Status and optional notes
   */
  markAttendance(sessionId: number | string, attendanceId: number, data: MarkAttendancePayload) {
    return this.http.patch<SessionAttendanceRecord>(
      API_SESSION_ATTENDANCE_MARK
        .replace('<SESSION_ID>', sessionId.toString())
        .replace('<ATTENDANCE_ID>', attendanceId.toString()),
      data
    );
  }

  /**
   * Bulk mark multiple attendance records (instructor only)
   * @param sessionId - The session ID
   * @param data - Attendance IDs, status, and optional notes
   */
  bulkMarkAttendance(sessionId: number | string, data: BulkMarkAttendancePayload) {
    return this.http.post<BulkMarkAttendanceResponse>(
      API_SESSION_ATTENDANCE_BULK_MARK.replace('<SESSION_ID>', sessionId.toString()),
      data
    );
  }

  /**
   * Get session analytics dashboard data (instructor only)
   * @param sessionId - The session ID
   */
  getSessionAnalytics(sessionId: number | string) {
    return this.http.get<SessionAnalytics>(
      API_SESSION_ANALYTICS.replace('<SESSION_ID>', sessionId.toString())
    );
  }

  /**
   * Get AI summary for a session
   * @param sessionId - The session ID
   */
  getSessionAiSummary(sessionId: number | string) {
    return this.http.get<any>(
      API_SESSION_AI_SUMMARY.replace('<SESSION_ID>', sessionId.toString())
    );
  }

  /**
   * Get AI summary for a session (student)
   * @param sessionId - The session ID
   */
  getStudentSessionAiSummary(sessionId: number | string) {
    return this.http.get<any>(
      API_STUDENT_SESSION_AI_SUMMARY.replace('<SESSION_ID>', sessionId.toString())
    );
  }

  /**
   * Get AI transcripts for a session (instructor)
   * @param sessionId - The session ID
   */
  getSessionAiTranscripts(sessionId: number | string) {
    return this.http.get<any>(
      API_SESSION_AI_TRANSCRIPTS.replace('<SESSION_ID>', sessionId.toString())
    );
  }

  /**
   * Get AI transcripts for a session (student)
   * @param sessionId - The session ID
   */
  getStudentSessionAiTranscripts(sessionId: number | string) {
    return this.http.get<any>(
      API_STUDENT_SESSION_AI_TRANSCRIPTS.replace('<SESSION_ID>', sessionId.toString())
    );
  }

  /**
   * Update AI summary for a session
   * @param sessionId - The session ID
   * @param aiSummary - The updated AI summary markdown text
   */
  updateSessionAiSummary(sessionId: number | string, aiSummary: string) {
    return this.http.patch<any>(
      API_SESSION_AI_SUMMARY.replace('<SESSION_ID>', sessionId.toString()),
      { ai_summary: aiSummary }
    );
  }

  /**
   * Get detailed attendance record with event timeline (instructor only)
   * @param sessionId - The session ID
   * @param attendanceId - The attendance record ID
   */
  getAttendanceDetail(sessionId: number | string, attendanceId: number) {
    return this.http.get<AttendanceDetail>(
      API_SESSION_ATTENDANCE_DETAIL
        .replace('<SESSION_ID>', sessionId.toString())
        .replace('<ATTENDANCE_ID>', attendanceId.toString())
    );
  }

  // ==================== Series Analytics ====================

  /**
   * Get series-level analytics with trends, punctuality, engagement (instructor only)
   * @param seriesId - The series ID
   */
  getSeriesAnalytics(seriesId: number | string) {
    return this.http.get<SeriesAnalytics>(
      API_SERIES_ANALYTICS.replace('<SERIES_ID>', seriesId.toString())
    );
  }

  /**
   * Get at-risk students for a series (instructor only)
   * @param seriesId - The series ID
   * @param params - Query params: risk_level (at_risk, needs_attention, all), ordering
   */
  getAtRiskStudents(seriesId: number | string, params: Params = {}) {
    return this.http.get<AtRiskStudentsResponse>(
      encodeURL(API_SERIES_AT_RISK_STUDENTS.replace('<SERIES_ID>', seriesId.toString()), params)
    );
  }

  // =========================================================================
  // Student Analytics APIs (Student-facing)
  // =========================================================================

  /**
   * Get student's own analytics for a series
   * Includes attendance metrics, comparison to class, streak, achievements, and banners
   * @param seriesId - The series ID
   */
  getStudentSeriesAnalytics(seriesId: number | string) {
    return this.http.get<StudentAnalyticsResponse>(
      API_STUDENT_SERIES_ANALYTICS.replace('<SERIES_ID>', seriesId.toString())
    );
  }

  /**
   * Get post-session recap data for a completed session
   * Includes attendance stats, punctuality, class rank, streak, and next session info
   * @param sessionId - The session ID
   */
  getSessionRecap(sessionId: number | string) {
    return this.http.get<PostSessionRecapResponse>(
      API_STUDENT_SESSION_RECAP.replace('<SESSION_ID>', sessionId.toString())
    );
  }

  /**
   * Get global analytics aggregated across all student's series
   * Includes total sessions, overall attendance rate, streaks, badges, and upcoming sessions
   */
  getGlobalStudentAnalytics() {
    return this.http.get<GlobalStudentAnalyticsResponse>(API_STUDENT_GLOBAL_ANALYTICS);
  }

  // =========================================================================
  // Calendar Export APIs
  // =========================================================================

  /**
   * Get calendar links for a session (Google Calendar URL, Outlook URL, ICS download URLs).
   * Only works for upcoming (SCHEDULED) sessions.
   */
  getSessionCalendarLinks(sessionId: number | string) {
    return this.http.get<SessionCalendarLinks>(
      API_SESSION_CALENDAR_LINKS.replace('<SESSION_ID>', sessionId.toString())
    );
  }
}

export interface SessionCalendarLinks {
  session_id: number;
  series_id: number;
  google_calendar_url: string;
  outlook_calendar_url: string;
  session_ics_url: string;
  series_ics_url: string;
}
